import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const FAQ: FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const faqData: FAQItem[] = [
    // General Questions
    {
      id: 1,
      question: "What is Dhoondh?",
      answer: "Dhoondh is a comprehensive job portal platform that connects job seekers with employers and recruitment agents. We use AI-powered matching to help you find the perfect job opportunities and build your dream team.",
      category: "general"
    },
    {
      id: 2,
      question: "How does Dhoondh work?",
      answer: "Dhoondh works by creating detailed profiles for job seekers and companies, then using our smart algorithms to match them based on skills, experience, and preferences. Job seekers pay ₹199 for premium matching services, while companies can post jobs for free.",
      category: "general"
    },
    {
      id: 3,
      question: "Is Dhoondh free to use?",
      answer: "For job seekers, the core platform is free to use but we offer a premium service that includes professional resume building and personalized recommendations. Companies can post jobs and access our platform for free. We also offer a 100% money-back guarantee within 6 months if no suitable job match is found.",
      category: "general"
    },

    // Job Seekers
    {
      id: 4,
      question: "How do I create a job seeker profile?",
      answer: "Creating a profile is easy! Simply register with your email and phone number, verify your account, and complete your profile with your professional information, skills, experience, and job preferences. Our guided setup process will help you create a compelling profile.",
      category: "jobseekers"
    },
    {
      id: 5,
      question: "What information should I include in my profile?",
      answer: "Include your professional headline, work experience, education, skills, job preferences, and upload your resume. The more detailed your profile, the better our AI can match you with relevant opportunities.",
      category: "jobseekers"
    },
    {
      id: 6,
      question: "How does the AI job matching work?",
      answer: "Our AI analyzes your profile, skills, experience, and preferences to find jobs that match your criteria. It considers factors like job requirements, company culture, location preferences, and career goals to provide personalized recommendations.",
      category: "jobseekers"
    },
    {
      id: 7,
      question: "Can I apply for jobs directly through Dhoondh?",
      answer: "Yes! You can browse suggested job listings, view detailed job descriptions, and apply directly through our platform. Your application will be sent to the employer, and you can track your application status in your dashboard.",
      category: "jobseekers"
    },
    {
      id: 8,
      question: "What if I don't find a suitable job?",
      answer: "We offer a 100% money-back guarantee within 6 months if no suitable job match is found. Our team is also available to help optimize your profile and provide career guidance to improve your chances of finding the right opportunity.",
      category: "jobseekers"
    },

    // Companies
    {
      id: 9,
      question: "How do I register my company?",
      answer: "Company registration is free! Simply create an account, provide your company details, verify your business information, and start posting jobs. Our verification process ensures quality and authenticity on our platform.",
      category: "companies"
    },
    {
      id: 10,
      question: "What documents do I need for company verification?",
      answer: "You'll need business registration documents, GST certificate, PAN card, and company address proof. We may also request additional documents based on your business type and industry.",
      category: "companies"
    },
    {
      id: 11,
      question: "How much does it cost to post jobs?",
      answer: "Job posting is completely free for companies! You can post unlimited jobs, manage applications, and access our candidate database at no cost. We believe in making hiring accessible for all businesses.",
      category: "companies"
    },
    {
      id: 12,
      question: "How do I manage job applications?",
      answer: "Our dashboard provides a comprehensive view of all applications. You can review candidate profiles, shortlist applicants, schedule interviews, and communicate with candidates directly through our platform.",
      category: "companies"
    },
    {
      id: 13,
      question: "Can I invite team members to help with hiring?",
      answer: "Yes! You can invite HR team members, hiring managers, and other stakeholders to collaborate on the hiring process. Each team member can have different permissions and access levels.",
      category: "companies"
    },

    // Payment & Billing
    {
      id: 14,
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, debit cards, net banking, UPI, and digital wallets through our secure payment partner Razorpay. All transactions are encrypted and secure.",
      category: "payment"
    },
    {
      id: 15,
      question: "Is my payment information secure?",
      answer: "Absolutely! We use industry-standard encryption and partner with Razorpay, a PCI DSS compliant payment processor, to ensure your payment information is always secure and protected.",
      category: "payment"
    },
    {
      id: 16,
      question: "What is your refund policy?",
      answer: "We offer a 100% money-back guarantee within 6 months if no suitable job match is found for job seekers. For companies, since job posting is free, there are no charges to refund. Refund requests are processed within 5-7 business days.",
      category: "payment"
    },
    {
      id: 17,
      question: "Do you provide invoices for payments?",
      answer: "Yes, we provide detailed invoices for all payments. You can download invoices from your account dashboard or request them via email. All invoices include GST details for business users.",
      category: "payment"
    },

    // Technical Support
    {
      id: 18,
      question: "How do I reset my password?",
      answer: "Click on 'Forgot Password' on the login page, enter your registered email or phone number, and follow the instructions sent to you. You can also contact our support team for assistance.",
      category: "technical"
    },
    {
      id: 19,
      question: "Can I use Dhoondh on mobile devices?",
      answer: "Yes! Dhoondh is fully responsive and works seamlessly on all devices including smartphones, tablets, and desktops. You can also download our mobile app for an enhanced experience.",
      category: "technical"
    },
    {
      id: 20,
      question: "What browsers are supported?",
      answer: "Dhoondh works on all modern browsers including Chrome, Firefox, Safari, Edge, and Opera. We recommend using the latest version of your preferred browser for the best experience.",
      category: "technical"
    },
    {
      id: 21,
      question: "How do I contact customer support?",
      answer: "You can reach our support team via email at operations@dhoondh.in, phone at +91 93168 46322, or through the contact form on our website. We typically respond within 24 hours.",
      category: "technical"
    },

    // Privacy & Security
    {
      id: 22,
      question: "How do you protect my personal information?",
      answer: "We implement industry-standard security measures including encryption, secure servers, and regular security audits. Your personal information is never shared without your consent, and we comply with all applicable data protection laws.",
      category: "privacy"
    },
    {
      id: 23,
      question: "Can I delete my account?",
      answer: "Yes, you can delete your account at any time from your account settings. This will permanently remove your profile and data from our platform. Please note that this action cannot be undone.",
      category: "privacy"
    },
    {
      id: 24,
      question: "Who can see my profile information?",
      answer: "Your profile visibility depends on your privacy settings. You can choose to make your profile visible to all employers, only to companies you apply to, or keep it private. You have full control over your information sharing.",
      category: "privacy"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions', count: faqData.length },
    { id: 'general', name: 'General', count: faqData.filter(item => item.category === 'general').length },
    { id: 'jobseekers', name: 'Job Seekers', count: faqData.filter(item => item.category === 'jobseekers').length },
    { id: 'companies', name: 'Companies', count: faqData.filter(item => item.category === 'companies').length },
    { id: 'payment', name: 'Payment & Billing', count: faqData.filter(item => item.category === 'payment').length },
    { id: 'technical', name: 'Technical Support', count: faqData.filter(item => item.category === 'technical').length },
    { id: 'privacy', name: 'Privacy & Security', count: faqData.filter(item => item.category === 'privacy').length }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory);

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-teaGreen/5 via-white to-primary-lightGreen/10">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-teaGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-black mb-6 leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Find answers to common questions about AMPS DHOONDH RECRUITMENT LLP ("Dhoondh") and our services.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            
            {/* Category Filter */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-6">Browse by Category</h2>
              <div className="flex flex-wrap gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-primary-lightGreen text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-primary-teaGreen/20 hover:text-primary-black'
                    }`}
                  >
                    {category.name} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  className="w-full px-4 py-3 pl-12 rounded-xl border-2 border-gray-200 focus:border-primary-lightGreen focus:outline-none transition-colors"
                />
                <svg
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {filteredFAQs.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg"
                >
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-300 flex justify-between items-center"
                  >
                    <h3 className="text-lg font-semibold text-primary-black pr-4">
                      {item.question}
                    </h3>
                    <svg
                      className={`h-5 w-5 text-primary-lightGreen transition-transform duration-300 flex-shrink-0 ${
                        expandedItems.has(item.id) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedItems.has(item.id) && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredFAQs.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
                <p className="text-gray-500">Try selecting a different category or contact our support team.</p>
              </div>
            )}

            {/* Contact Support */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="bg-primary-teaGreen/10 rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-primary-black mb-4">
                  Still have questions?
                </h2>
                <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                  Our support team is here to help! Get in touch with us and we'll get back to you within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button size="lg">
                      Contact Support
                    </Button>
                  </Link>
                  <a href="mailto:operations@dhoondh.in">
                    <Button variant="outline" size="lg">
                      Email Us
                    </Button>
                  </a>
                </div>
                <div className="mt-6 text-sm text-gray-600">
                  <p>operations@dhoondh.in</p>
                  <p>+91 93168 46322</p>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="text-center pt-8">
              <Link to="/">
                <Button variant="outline" size="lg">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
